import React, { useState, useEffect } from "react";
import { db, ref, onValue } from "./firebase";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore from "swiper";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/swiper-bundle.css";

// Initialize Swiper modules
SwiperCore.use([Navigation, Pagination]);

const Dashboard = () => {
  const [courses, setCourses] = useState({});
  const [homeworks, setHomeworks] = useState({});
  const [registrationRequests, setRegistrationRequests] = useState([]);

  // Fetch data from Firebase
  useEffect(() => {
    // Fetch all courses and their data
    const coursesRef = ref(db, "courses");
    onValue(coursesRef, (snapshot) => {
      if (snapshot.exists()) {
        const coursesData = snapshot.val();
        setCourses(coursesData);

        // Fetch homeworks for each course (only due assignments)
        const homeworksData = {};
        const now = new Date();

        Object.entries(coursesData).forEach(([course, data]) => {
          if (data.homeworks) {
            const dueHomeworks = Object.values(data.homeworks).filter(hw => {
              if (!hw.dueDate) return false;
              const dueDate = new Date(hw.dueDate);
              return dueDate >= now;
            });
            homeworksData[course] = dueHomeworks;
          }
        });

        setHomeworks(homeworksData);
      }
    });

    // Fetch registration requests
    const registrationRef = ref(db, "registrationRequests");
    onValue(registrationRef, (snapshot) => {
      if (snapshot.exists()) {
        const requests = Object.entries(snapshot.val()).map(([id, data]) => ({
          id,
          ...data
        }));
        setRegistrationRequests(requests);
      } else {
        setRegistrationRequests([]);
      }
    });
  }, []);

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-gray-900">Admin Dashboard</h1>

      {/* Grid Layout for Dashboard Boxes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* Box 1: Syllabus Completion for All Courses */}
        <div className="bg-white p-4 sm:p-6 shadow-lg rounded-xl border border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-900">Syllabus Completion</h3>
          <div className="relative">
            <Swiper
              spaceBetween={20}
              slidesPerView={1}
              navigation={{
                nextEl: '.swiper-button-next-syllabus',
                prevEl: '.swiper-button-prev-syllabus',
              }}
              pagination={{ clickable: true, el: ".custom-pagination-syllabus" }}
            >
              {Object.entries(courses)
                .sort(([, a], [, b]) => a.index - b.index)
                .map(([course, data]) => {
                  const completedChapters = Object.values(data.chapters || {}).filter((ch) => ch.done).length;
                  const totalChapters = Object.keys(data.chapters || {}).length;
                  const completionPercentage = totalChapters > 0 ? Math.round((completedChapters / totalChapters) * 100) : 0;

                  return (
                    <SwiperSlide key={course}>
                      <div className="flex flex-col px-4 sm:px-6 md:px-8">
                        <div className="flex justify-between items-center mb-2 gap-2">
                          <h4 className="text-sm sm:text-base font-semibold text-gray-900 break-words flex-1">{course}</h4>
                          <span className="text-sm font-medium text-teal-600 flex-shrink-0">{completionPercentage}%</span>
                        </div>
                        <div className="w-full h-3 sm:h-4 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-teal-600 rounded-full transition-all duration-500"
                            style={{ width: `${completionPercentage}%` }}
                          />
                        </div>
                        <div className="mt-2 text-xs sm:text-sm text-gray-600">
                          {completedChapters} of {totalChapters} chapters completed
                        </div>
                      </div>
                    </SwiperSlide>
                  );
                })}
            </Swiper>
            <div className="swiper-button-prev-syllabus absolute left-0 top-1/2 -translate-y-1/2 z-10 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-white rounded-full shadow-md cursor-pointer hover:bg-gray-50">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
            <div className="swiper-button-next-syllabus absolute right-0 top-1/2 -translate-y-1/2 z-10 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-white rounded-full shadow-md cursor-pointer hover:bg-gray-50">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <div className="custom-pagination-syllabus mt-3 sm:mt-4 flex justify-center space-x-2"></div>
          </div>
        </div>

        {/* Box 2: Due Assignments for All Courses */}
        <div className="bg-white p-4 sm:p-6 shadow-lg rounded-xl border border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-900">Due Assignments</h3>
          <div className="relative">
            <Swiper
              spaceBetween={20}
              slidesPerView={1}
              navigation={{
                nextEl: '.swiper-button-next-assignments',
                prevEl: '.swiper-button-prev-assignments',
              }}
              pagination={{ clickable: true, el: ".custom-pagination-assignments" }}
            >
              {Object.entries(homeworks).map(([course, assignments]) => (
                <SwiperSlide key={course}>
                  <div className="flex flex-col px-4 sm:px-6 md:px-8">
                    <h4 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-gray-900 break-words">{course}</h4>
                    <div className="space-y-2 sm:space-y-3">
                      {assignments.length > 0 ? (
                        assignments.map((hw, index) => (
                          <div key={index} className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-1 sm:gap-0">
                              <span className="font-medium text-gray-900 text-sm sm:text-base break-words">{hw.title}</span>
                              <span className="text-xs sm:text-sm text-gray-600 flex-shrink-0">{new Date(hw.dueDate).toLocaleDateString()}</span>
                            </div>
                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-green-500 rounded-full"
                                style={{ width: "100%" }}
                              />
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-center py-4 text-sm sm:text-base">No assignments due.</p>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="swiper-button-prev-assignments absolute left-0 top-1/2 -translate-y-1/2 z-10 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-white rounded-full shadow-md cursor-pointer hover:bg-gray-50">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
            <div className="swiper-button-next-assignments absolute right-0 top-1/2 -translate-y-1/2 z-10 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-white rounded-full shadow-md cursor-pointer hover:bg-gray-50">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <div className="custom-pagination-assignments mt-3 sm:mt-4 flex justify-center space-x-2"></div>
          </div>
        </div>

        {/* Box 3: Registration Requests */}
        <div className="bg-white p-4 sm:p-6 shadow-lg rounded-xl border border-gray-200 md:col-span-2">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-900">Registration Requests</h3>
          <div className="flex flex-col">
            <div className="text-4xl sm:text-5xl font-bold text-teal-600 mb-2">
              {registrationRequests.length}
            </div>
            <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">Pending registrations</p>
            <div className="w-full">
              {/* Mobile Card View */}
              <div className="block sm:hidden space-y-3">
                {registrationRequests.slice(0, 3).map((request) => (
                  <div key={request.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm break-words">{request.name}</p>
                        <p className="text-xs text-gray-600 mt-1 break-words">{request.course}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs flex-shrink-0 ml-2 ${request.status === "Pending" ? "bg-yellow-100 text-yellow-800" :
                        request.status === "Registered" ? "bg-green-100 text-green-800" :
                          request.status === "Contacted" ? "bg-teal-100 text-teal-800" :
                            "bg-red-100 text-red-800"
                        }`}>
                        {request.status || "Pending"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium text-gray-700">Name</th>
                      <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium text-gray-700">Course</th>
                      <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-medium text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrationRequests.slice(0, 3).map((request) => (
                      <tr key={request.id} className="border-t border-gray-100">
                        <td className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 break-words">{request.name}</td>
                        <td className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 break-words">{request.course}</td>
                        <td className="px-3 sm:px-4 py-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${request.status === "Pending" ? "bg-yellow-100 text-yellow-800" :
                            request.status === "Registered" ? "bg-green-100 text-green-800" :
                              request.status === "Contacted" ? "bg-teal-100 text-teal-800" :
                                "bg-red-100 text-red-800"
                            }`}>
                            {request.status || "Pending"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {registrationRequests.length > 3 && (
                <p className="text-xs sm:text-sm text-gray-500 mt-2 sm:mt-3">
                  + {registrationRequests.length - 3} more requests
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;